import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { Alert, Avatar, Empty, Muted, PageHeader, Section } from '@/components/ui'
import { fetchColaboradorTimeline, fetchColaboradores } from './api'
import type { ColaboradorResumo } from './types'
import { formatDate } from './utils'

export function TimelinePage() {
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [selecionado, setSelecionado] = useState<ColaboradorResumo | null>(null)

  const buscou = q.length > 0

  const lista = useQuery({
    queryKey: ['rh', 'colaboradores', 'timeline-picker', q],
    queryFn: () =>
      fetchColaboradores({
        busca: q,
        status: 'ativo',
        page: 1,
        por_pagina: 15,
      }),
    enabled: buscou,
  })

  const timeline = useQuery({
    queryKey: ['rh', 'timeline', selectedId],
    queryFn: () => fetchColaboradorTimeline(selectedId),
    enabled: Boolean(selectedId),
  })

  const dados = lista.data?.dados || []

  return (
    <section className="hi-page">
      <PageHeader
        title="Timeline"
        subtitle="Busque um colaborador para ver a linha do tempo. Editor avançado no legado."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/timeline')}>
            Legado
          </a>
        }
      />

      <form
        className="hi-filters"
        onSubmit={(e) => {
          e.preventDefault()
          const termo = search.trim()
          setQ(termo)
          setSelectedId('')
          setSelecionado(null)
        }}
      >
        <input
          type="search"
          placeholder="Nome, matrícula, e-mail…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={!search.trim()}>
          Buscar
        </button>
        {buscou ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setSearch('')
              setQ('')
              setSelectedId('')
              setSelecionado(null)
            }}
          >
            Limpar
          </button>
        ) : null}
      </form>

      {lista.isError ? (
        <Alert tone="danger">
          {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar.'}
        </Alert>
      ) : null}

      <div className="hi-split" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0,1fr)' }}>
        <Section title="Colaboradores" flush>
          {!buscou ? (
            <Muted>Digite um termo e clique em Buscar para listar colaboradores.</Muted>
          ) : null}
          {buscou && lista.isLoading ? <Muted>Buscando…</Muted> : null}
          {buscou && !lista.isLoading && dados.length === 0 ? (
            <Empty>Nenhum colaborador encontrado para “{q}”.</Empty>
          ) : null}
          {buscou && dados.length > 0 ? (
            <ul className="hi-list">
              {dados.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`card card-interactive hi-row${selectedId === c.id ? ' is-active' : ''}`}
                    style={{ width: '100%', textAlign: 'left', cursor: 'pointer', border: 'none' }}
                    onClick={() => {
                      setSelectedId(c.id)
                      setSelecionado(c)
                    }}
                  >
                    <Avatar src={c.foto_url || c.foto} nome={c.nome} iniciais={c.iniciais} />
                    <div className="hi-row-main">
                      <strong>{c.nome}</strong>
                      <span>
                        {c.cargo_nome || 'Sem cargo'}
                        {c.departamento_nome ? ` · ${c.departamento_nome}` : ''}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </Section>

        <Section
          title={selecionado ? `Eventos — ${selecionado.nome}` : 'Eventos'}
          actions={
            selectedId ? (
              <Link className="btn btn-secondary btn-sm" to={`/app/rh/${selectedId}`}>
                Perfil
              </Link>
            ) : null
          }
        >
          {!selectedId ? (
            <Muted>
              {buscou
                ? 'Selecione um colaborador na lista para ver a timeline.'
                : 'Faça uma busca e selecione um colaborador.'}
            </Muted>
          ) : null}
          {selectedId && timeline.isLoading ? <Muted>Carregando…</Muted> : null}
          {selectedId && timeline.isError ? (
            <Alert tone="danger">
              {timeline.error instanceof ApiError
                ? timeline.error.message
                : 'Timeline indisponível.'}
            </Alert>
          ) : null}
          {selectedId && !timeline.isLoading && (timeline.data || []).length === 0 ? (
            <Empty>Sem eventos registrados.</Empty>
          ) : null}
          {(timeline.data || []).length > 0 ? (
            <ul className="hi-feed">
              {(timeline.data || []).map((ev, i) => (
                <li key={i}>
                  <strong>
                    {ev.icon ? `${String(ev.icon)} ` : ''}
                    {String(ev.titulo || ev.tipo || 'Evento')}
                  </strong>
                  <span>
                    {formatDate(String(ev.data || ''))}
                    {ev.autor ? ` · por ${String(ev.autor)}` : ''}
                  </span>
                  {ev.descricao ? (
                    <span style={{ display: 'block', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                      {String(ev.descricao)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </Section>
      </div>
    </section>
  )
}

export default TimelinePage
